import { useState, useEffect, useCallback } from 'react';
import { useAccessToken } from './useAuth';
import { useAlert } from '../context/AlertContext';
import { noteTypeService } from '../services/noteTypeService';

/**
 * Custom hook for managing note types
 * @param {Object} options - Configuration options
 * @param {string} options.module - Filter by specific module
 * @param {boolean} options.activeOnly - Return only active note types
 * @param {boolean} options.autoFetch - Automatically fetch data on mount
 * @returns {Object} Hook return value
 */
export const useNoteTypes = (options = {}) => {
    const { module, activeOnly = false, autoFetch = true } = options;
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();
    
    const [noteTypes, setNoteTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch note types
    const fetchNoteTypes = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            let data;

            if (module) {
                data = await noteTypeService.getByModule(accessToken, module);
            } else {
                data = await noteTypeService.getAll(accessToken, activeOnly);
            }

            setNoteTypes(data);
            return data;
        } catch (err) {
            console.error('Error fetching note types:', err);
            const errorMessage = err.message || 'Failed to load note types';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [module, activeOnly, getBackendAccessToken, setErrorAlert]);

    // Create note type
    const createNoteType = useCallback(async (noteTypeData) => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            const newNoteType = await noteTypeService.create(accessToken, noteTypeData);
            setInfoAlert('Note type created successfully');
            
            // Refresh the list
            await fetchNoteTypes();
            
            return newNoteType;
        } catch (err) {
            console.error('Error creating note type:', err);
            const errorMessage = err.message || 'Failed to create note type';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchNoteTypes, setInfoAlert, setErrorAlert]);

    // Update note type
    const updateNoteType = useCallback(async (id, noteTypeData) => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            const updatedNoteType = await noteTypeService.update(accessToken, id, noteTypeData);
            setInfoAlert('Note type updated successfully');
            
            // Refresh the list
            await fetchNoteTypes();
            
            return updatedNoteType;
        } catch (err) {
            console.error('Error updating note type:', err);
            const errorMessage = err.message || 'Failed to update note type';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchNoteTypes, setInfoAlert, setErrorAlert]);

    // Delete note type
    const deleteNoteType = useCallback(async (id) => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            const result = await noteTypeService.delete(accessToken, id);
            setInfoAlert('Note type deleted successfully');
            
            // Refresh the list
            await fetchNoteTypes();
            
            return result;
        } catch (err) {
            console.error('Error deleting note type:', err);
            const errorMessage = err.message || 'Failed to delete note type';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchNoteTypes, setInfoAlert, setErrorAlert]);

    // Update note visibility
    const updateModuleAvailability = useCallback(async (id, modules) => {
        try {
            setLoading(true);
            setError(null);
            
            const accessToken = await getBackendAccessToken();
            const result = await noteTypeService.updateModuleAvailability(accessToken, id, modules);
            setInfoAlert('Module visibility updated successfully');
            
            // Refresh the list
            await fetchNoteTypes();
            
            return result;
        } catch (err) {
            console.error('Error updating note visibility:', err);
            const errorMessage = err.message || 'Failed to update module visibility';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchNoteTypes, setInfoAlert, setErrorAlert]);

    // Enable module
    const enableModule = useCallback(async (id, moduleName) => {
        try {
            setLoading(true);
            setError(null);
            const accessToken = await getBackendAccessToken();
            const result = await noteTypeService.enableModule(accessToken, id, moduleName);
            setInfoAlert(`Note type enabled for ${moduleName}`);
            await fetchNoteTypes();
            return result;
        } catch (err) {
            console.error('Error enabling module:', err);
            const errorMessage = err.message || 'Failed to enable module';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchNoteTypes, setInfoAlert, setErrorAlert]);

    // Disable module
    const disableModule = useCallback(async (id, moduleName) => {
        try {
            setLoading(true);
            setError(null);
            const accessToken = await getBackendAccessToken();
            const result = await noteTypeService.disableModule(accessToken, id, moduleName);
            setInfoAlert(`Note type disabled for ${moduleName}`);
            await fetchNoteTypes();
            return result;
        } catch (err) {
            console.error('Error disabling module:', err);
            const errorMessage = err.message || 'Failed to disable module';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, fetchNoteTypes, setInfoAlert, setErrorAlert]);

    // Get statistics
    const getStatistics = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const accessToken = await getBackendAccessToken();
            const stats = await noteTypeService.getStatistics(accessToken);
            return stats;
        } catch (err) {
            console.error('Error fetching statistics:', err);
            const errorMessage = err.message || 'Failed to load statistics';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    // Search note types
    const searchNoteTypes = useCallback(async (criteria) => {
        try {
            setLoading(true);
            setError(null);
            const accessToken = await getBackendAccessToken();
            const results = await noteTypeService.search(accessToken, criteria);
            setNoteTypes(results);
            return results;
        } catch (err) {
            console.error('Error searching note types:', err);
            const errorMessage = err.message || 'Failed to search note types';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    // Auto-fetch on mount
    useEffect(() => {
        if (autoFetch) {
            fetchNoteTypes();
        }
    }, [autoFetch, fetchNoteTypes]);

    return {
        noteTypes,
        loading,
        error,
        fetchNoteTypes,
        createNoteType,
        updateNoteType,
        deleteNoteType,
        updateModuleAvailability,
        enableModule,
        disableModule,
        getStatistics,
        searchNoteTypes,
        refetch: fetchNoteTypes
    };
};

/**
 * Hook for getting note type statistics
 * @returns {Object} Statistics hook return value
 */
export const useNoteTypeStatistics = () => {
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert } = useAlert();
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Fetch statistics from backend
    const fetchStatistics = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const accessToken = await getBackendAccessToken();
            const stats = await noteTypeService.getStatistics(accessToken);
            setStatistics(stats);
            return stats;
        } catch (err) {
            console.error('Error fetching note type statistics:', err);
            const errorMessage = err.message || 'Failed to load statistics';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    // Auto-fetch on mount
    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    // Calculate total note types from statistics
    const totalNoteTypes = statistics
        ? Object.values(statistics).reduce((sum, count) => sum + count, 0)
        : 0;

    return {
        statistics,
        loading,
        error,
        totalNoteTypes,
        refetch: fetchStatistics
    };
};

export default useNoteTypes;
